import { useState } from 'react';
import { useQuote } from '../../contexts/QuoteContext';

interface DragItem {
  id: string;
  index: number;
}

export const WorkflowEditor = () => {
  const { currentWorkflow, updateWorkflowStage, reorderWorkflowStages, saveWorkflow, isSaving, setCurrentWorkflow } = useQuote();
  const [draggedItem, setDraggedItem] = useState<DragItem | null>(null);
  const [showAddStageForm, setShowAddStageForm] = useState(false);
  const [newStageData, setNewStageData] = useState({
    title: '',
    persona: 'DEAL_DESK'
  });

  const personas = [
    { value: 'AE', label: 'Account Executive' },
    { value: 'DEAL_DESK', label: 'Deal Desk' },
    { value: 'CRO', label: 'Chief Revenue Officer' },
    { value: 'LEGAL', label: 'Legal' },
    { value: 'FINANCE', label: 'Finance' },
    { value: 'CUSTOMER', label: 'Customer' }
  ];

  const handleShowAddForm = () => {
    setShowAddStageForm(true);
  };

  const handleAddStage = () => {
    if (!currentWorkflow || !newStageData.title.trim()) return;
    
    const maxOrder = Math.max(...currentWorkflow.stages.map(s => s.order));
    const newStage = {
      id: `stage_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      title: newStageData.title.trim(),
      description: '',
      completed: false,
      order: maxOrder + 1,
      connectedPersona: newStageData.persona,
      timeCreated: new Date()
    };
    
    setCurrentWorkflow({
      ...currentWorkflow,
      stages: [...currentWorkflow.stages, newStage]
    });

    // Reset form
    setNewStageData({ title: '', persona: 'DEAL_DESK' });
    setShowAddStageForm(false);
  };

  const handleCancelAdd = () => {
    setNewStageData({ title: '', persona: 'DEAL_DESK' });
    setShowAddStageForm(false);
  };

  const removeStage = (stageId: string) => {
    if (!currentWorkflow) return;
    
    const newStages = currentWorkflow.stages.filter(s => s.id !== stageId);
    setCurrentWorkflow({
      ...currentWorkflow,
      stages: newStages
    });
  };

  if (!currentWorkflow) {
    return (
      <div className="workflow-editor">
        <h3>No workflow found</h3>
        <p>This quote doesn't have an associated workflow.</p>
      </div>
    );
  }

  const handleDragStart = (e: React.DragEvent, stageId: string, index: number) => {
    setDraggedItem({ id: stageId, index });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (!draggedItem) return;
    
    const stages = [...currentWorkflow.stages];
    const draggedStage = stages[draggedItem.index];
    
    // Remove from old position
    stages.splice(draggedItem.index, 1);
    
    // Insert at new position
    stages.splice(dropIndex, 0, draggedStage);
    
    // Update the workflow with reordered stages
    reorderWorkflowStages(stages);
    setDraggedItem(null);
  };

  const handleStageToggle = (stageId: string, completed: boolean) => {
    updateWorkflowStage(stageId, { completed });
  };

  const handleSaveWorkflow = async () => {
    try {
      await saveWorkflow();
    } catch (error) {
      alert('Failed to save workflow: ' + error);
    }
  };

  return (
    <div className="workflow-editor">
      <div className="workflow-header">
        <h3>Workflow: {currentWorkflow.title}</h3>
      </div>
      <div className="workflow-actions">
        <button 
          onClick={handleShowAddForm}
          className="add-stage-button"
        >
          ➕ Add Stage
        </button>
        <button 
          onClick={handleSaveWorkflow} 
          className="save-workflow-button"
          disabled={isSaving}
        >
          {isSaving ? '💾 Saving...' : '💾 Save Workflow'}
        </button>
      </div>

      {showAddStageForm && (
        <div className="add-stage-form">
          <h4>Add New Stage</h4>
          <div className="form-row">
            <div className="form-group">
              <label>Stage Title</label>
              <input
                type="text"
                value={newStageData.title}
                onChange={(e) => setNewStageData({...newStageData, title: e.target.value})}
                placeholder="Enter stage title"
                className="stage-title-input"
              />
            </div>
            <div className="form-group">
              <label>Assigned To</label>
              <select
                value={newStageData.persona}
                onChange={(e) => setNewStageData({...newStageData, persona: e.target.value})}
                className="persona-select"
              >
                {personas.map(persona => (
                  <option key={persona.value} value={persona.value}>
                    {persona.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-actions">
            <button onClick={handleCancelAdd} className="cancel-button">
              Cancel
            </button>
            <button 
              onClick={handleAddStage} 
              className="confirm-add-button"
              disabled={!newStageData.title.trim()}
            >
              Add Stage
            </button>
          </div>
        </div>
      )}

      <div className="workflow-stages">
        {(currentWorkflow.stages || [])
          .sort((a, b) => a.order - b.order)
          .filter(stage => stage.order > 0) // Hide the "New Quote Created" stage (order 0)
          .map((stage, index) => (
            <div
              key={stage.id}
              className={`workflow-stage ${stage.completed ? 'completed' : 'pending'}`}
              draggable
              onDragStart={(e) => handleDragStart(e, stage.id, index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
            >
              <div className="stage-drag-handle"></div>
              
              <div className="stage-content">
                <div className="stage-header">
                  <div className="stage-info">
                    <div className="stage-title">{stage.title}</div>
                    <div className="stage-persona">{stage.connectedPersona}</div>
                    <div className="stage-timing">
                      <div className="time-created">
                        Created: {new Date(stage.timeCreated).toLocaleDateString()}
                      </div>
                      {stage.timeCompleted && (
                        <div className="time-completed">
                          Completed: {new Date(stage.timeCompleted).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {stage.description && (
                  <div className="stage-description">{stage.description}</div>
                )}
              </div>
              
              <div className="stage-actions">
                <label className="stage-checkbox">
                  <input
                    type="checkbox"
                    checked={stage.completed}
                    onChange={(e) => handleStageToggle(stage.id, e.target.checked)}
                  />
                  <span className="checkmark"></span>
                </label>
                <button 
                  onClick={() => removeStage(stage.id)}
                  className="remove-stage-button"
                  title="Remove this stage"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
      </div>
      
      {(currentWorkflow.stages || []).filter(s => s.order > 0).length > 0 && (
        <div className="workflow-progress">
          <div className="progress-label">
            Progress: {(currentWorkflow.stages || []).filter(s => s.completed && s.order > 0).length} / {(currentWorkflow.stages || []).filter(s => s.order > 0).length} stages completed
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ 
                width: `${((currentWorkflow.stages || []).filter(s => s.completed && s.order > 0).length / (currentWorkflow.stages || []).filter(s => s.order > 0).length) * 100 || 0}%` 
              }}
            />
          </div>
        </div>
      )}
      
      {(currentWorkflow.stages || []).filter(s => s.order > 0).length === 0 && (
        <div className="empty-workflow">
          <p>No workflow stages yet. Add your first stage to get started!</p>
        </div>
      )}
    </div>
  );
};